/**
 * Where a frame pinned to the desktop goes (plan 12 §3, §8), pure. Free move
 * with a soft snap on release, never on top of something: an Apple widget, a
 * desktop icon or another pin. A widget keeps its size (a widget size, or
 * any for a type sized freely) unless it is larger than its display's
 * margins hold; only its position settles. Rects are logical points in global
 * desktop coordinates; a display's work area is inset to its margin lines.
 */
import { WIDGET_SIZES } from "@/components/shapes/shared/widget-face";
import type { OccupiedRect, PinRect } from "./desktop-pin-contract";

/**
 * Apple's desktop widget grid, measured on macOS (plan 12 §8 Sizes): a 164 pt
 * card inside its 180 pt window's 8 pt shadow margin (`desktop_occupied.rs`),
 * cards 16 pt apart. Where the user put Apple's widgets decides where its
 * grid runs, so on a display with an Apple widget the margin lines are that
 * grid's lines nearest the work area's top-left, and a pin's card lines up
 * with Apple's column and rows. One cell (a small card and its gap) is 180 pt.
 */
/** The inset to the cards on a display without an Apple widget. */
export const PIN_MARGIN = 24;
/** Between neighbours (Apple's widget spacing). */
export const PIN_GUTTER = 16;
/** One grid cell: a small card and its gap. */
export const PIN_CELL = WIDGET_SIZES.small.width + PIN_GUTTER;
/** An edge this close to the margin line or a neighbour's gutter snaps to it. */
export const PIN_SNAP_DISTANCE = 12;

/** Something on the desktop: an occupied rect, or a pin by its record. */
type Occupant = PinRect & { kind?: OccupiedRect["kind"] };

interface PinSize {
  width: number;
  height: number;
}

/** The work area inside its margins, as edges. */
interface Bounds {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** The cell offset of Apple's grid from `start` along one axis, its card
 *  edges `edges` (none before `start`); `PIN_MARGIN` when there are none. */
function gridMargin(start: number, edges: readonly number[]): number {
  return edges.length === 0
    ? PIN_MARGIN
    : (Math.min(...edges) - start) % PIN_CELL;
}

function innerBounds(workArea: PinRect, occupied: readonly Occupant[]): Bounds {
  const widgets = occupied.filter(
    (rect) =>
      rect.kind === "widget" &&
      rect.x >= workArea.x &&
      rect.y >= workArea.y &&
      rect.x + rect.width <= workArea.x + workArea.width &&
      rect.y + rect.height <= workArea.y + workArea.height,
  );
  return {
    left:
      workArea.x +
      gridMargin(
        workArea.x,
        widgets.map((rect) => rect.x),
      ),
    top:
      workArea.y +
      gridMargin(
        workArea.y,
        widgets.map((rect) => rect.y),
      ),
    right: workArea.x + workArea.width - PIN_MARGIN,
    bottom: workArea.y + workArea.height - PIN_MARGIN,
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/** The rect inside the margins: moved in, and no larger than they hold. */
function clampIntoBounds(rect: PinRect, bounds: Bounds): PinRect {
  const width = Math.min(rect.width, Math.max(0, bounds.right - bounds.left));
  const height = Math.min(rect.height, Math.max(0, bounds.bottom - bounds.top));
  return {
    x: clamp(rect.x, bounds.left, bounds.right - width),
    y: clamp(rect.y, bounds.top, bounds.bottom - height),
    width,
    height,
  };
}

/** True when `a` keeps at least a gutter from `b`. */
function clears(a: PinRect, b: PinRect): boolean {
  return (
    a.x >= b.x + b.width + PIN_GUTTER ||
    a.x + a.width + PIN_GUTTER <= b.x ||
    a.y >= b.y + b.height + PIN_GUTTER ||
    a.y + a.height + PIN_GUTTER <= b.y
  );
}

function overlaps(a: PinRect, b: PinRect): boolean {
  return (
    a.x < b.x + b.width &&
    a.x + a.width > b.x &&
    a.y < b.y + b.height &&
    a.y + a.height > b.y
  );
}

function clearsAll(rect: PinRect, others: readonly PinRect[]): boolean {
  return others.every((other) => clears(rect, other));
}

function sortedUnique(values: readonly number[]): number[] {
  return [...new Set(values)].sort((a, b) => a - b);
}

/** The rect kept on its display: inside the margins. */
export function clampPinRect(
  rect: PinRect,
  workArea: PinRect,
  occupied: readonly Occupant[],
): PinRect {
  return clampIntoBounds(rect, innerBounds(workArea, occupied));
}

/** The grid lines from `start` to `end`, one cell apart. */
function gridLines(start: number, end: number): number[] {
  const lines: number[] = [];
  for (let line = start; line <= end; line += PIN_CELL) lines.push(line);
  return lines;
}

/**
 * A new pin's spot: from the work area's top-left (inside the margins) down
 * the column, then the next column, the first spot that keeps a gutter from
 * everything occupied. It tries the cells of Apple's widget grid on that
 * display and a gutter past each occupied rect's far edge (a pin moved off
 * the grid), so it lines up with its neighbours. No room: the top-left.
 */
export function firstFreeSpot(
  size: PinSize,
  workArea: PinRect,
  occupied: readonly Occupant[],
): PinRect {
  const bounds = innerBounds(workArea, occupied);
  const { width, height } = size;
  const columns = sortedUnique([
    ...gridLines(bounds.left, bounds.right - width),
    ...occupied.map((rect) => rect.x + rect.width + PIN_GUTTER),
  ]).filter((x) => x >= bounds.left && x + width <= bounds.right);
  const rows = sortedUnique([
    ...gridLines(bounds.top, bounds.bottom - height),
    ...occupied.map((rect) => rect.y + rect.height + PIN_GUTTER),
  ]).filter((y) => y >= bounds.top && y + height <= bounds.bottom);
  for (const x of columns) {
    for (const y of rows) {
      const spot = { x, y, width, height };
      if (clearsAll(spot, occupied)) return spot;
    }
  }
  return { x: bounds.left, y: bounds.top, width, height };
}

/** The neighbours level with `rect` along the other axis, near enough to snap to. */
function levelWith(
  rect: PinRect,
  others: readonly PinRect[],
  axis: "x" | "y",
): PinRect[] {
  const reach = PIN_GUTTER + PIN_SNAP_DISTANCE;
  return others.filter((other) =>
    axis === "x"
      ? other.y < rect.y + rect.height + reach &&
        other.y + other.height > rect.y - reach
      : other.x < rect.x + rect.width + reach &&
        other.x + other.width > rect.x - reach,
  );
}

/** The move (within the snap distance) that lands an edge on a target, or 0. */
function nearestSnap(
  edges: ReadonlyArray<{ at: number; targets: readonly number[] }>,
): number {
  let best = 0;
  let bestDistance = PIN_SNAP_DISTANCE + 1;
  for (const { at, targets } of edges) {
    for (const target of targets) {
      const distance = Math.abs(target - at);
      if (distance < bestDistance) {
        best = target - at;
        bestDistance = distance;
      }
    }
  }
  return best;
}

/**
 * Soft snap: the widget moves so an edge lands on a margin line, a
 * neighbour's gutter, or the matching edge of a neighbour above or below
 * (the same column) or beside it (the same row).
 */
function snap(
  rect: PinRect,
  bounds: Bounds,
  others: readonly PinRect[],
): PinRect {
  const columns = levelWith(rect, others, "x");
  const rows = levelWith(rect, others, "y");
  const right = {
    at: rect.x + rect.width,
    targets: [
      bounds.right,
      ...columns.map((other) => other.x - PIN_GUTTER),
      ...rows.map((other) => other.x + other.width),
    ],
  };
  const bottom = {
    at: rect.y + rect.height,
    targets: [
      bounds.bottom,
      ...rows.map((other) => other.y - PIN_GUTTER),
      ...columns.map((other) => other.y + other.height),
    ],
  };
  const left = {
    at: rect.x,
    targets: [
      bounds.left,
      ...columns.map((other) => other.x + other.width + PIN_GUTTER),
      ...rows.map((other) => other.x),
    ],
  };
  const top = {
    at: rect.y,
    targets: [
      bounds.top,
      ...rows.map((other) => other.y + other.height + PIN_GUTTER),
      ...columns.map((other) => other.y),
    ],
  };
  return {
    ...rect,
    x: rect.x + nearestSnap([left, right]),
    y: rect.y + nearestSnap([top, bottom]),
  };
}

/**
 * The free spot nearest `rect` (its size kept) inside the margins: the
 * smallest move that keeps a gutter from every other rect. Each coordinate
 * of the nearest spot is either unchanged, on a margin line or a gutter off
 * an occupied edge, so only those are tried. None: `null`.
 */
function nearestFreeSpot(
  rect: PinRect,
  bounds: Bounds,
  others: readonly PinRect[],
): PinRect | null {
  const { width, height } = rect;
  const xs = sortedUnique([
    rect.x,
    bounds.left,
    bounds.right - width,
    ...others.flatMap((other) => [
      other.x - PIN_GUTTER - width,
      other.x + other.width + PIN_GUTTER,
    ]),
  ]).filter((x) => x >= bounds.left && x + width <= bounds.right);
  const ys = sortedUnique([
    rect.y,
    bounds.top,
    bounds.bottom - height,
    ...others.flatMap((other) => [
      other.y - PIN_GUTTER - height,
      other.y + other.height + PIN_GUTTER,
    ]),
  ]).filter((y) => y >= bounds.top && y + height <= bounds.bottom);
  let best: PinRect | null = null;
  let bestDistance = Infinity;
  for (const x of xs) {
    for (const y of ys) {
      const spot = { x, y, width, height };
      const distance = Math.hypot(x - rect.x, y - rect.y);
      if (distance < bestDistance && clearsAll(spot, others)) {
        best = spot;
        bestDistance = distance;
      }
    }
  }
  return best;
}

/**
 * Where a widget settles after a move or resize: the soft snap, inside the
 * margins; then, if it comes closer than a gutter to another rect, the
 * nearest spot that keeps one. Its size stays.
 */
export function settlePinRect(
  rect: PinRect,
  workArea: PinRect,
  others: readonly Occupant[],
): PinRect {
  const bounds = innerBounds(workArea, others);
  const settled = clampIntoBounds(snap(rect, bounds, others), bounds);
  if (clearsAll(settled, others)) return settled;
  return nearestFreeSpot(settled, bounds, others) ?? settled;
}

/** A pin to lay out: its rect, exactly its widget size. */
export interface PinToLayOut {
  frameId: string;
  rect: PinRect;
}

export interface PinLayout {
  /** Pins on a connected display that move (or shrink into its margins),
   *  to keep: one that covered a pin before it takes the first free spot on
   *  its display. */
  moved: Map<string, PinRect>;
  /** Where a pin whose display is not connected shows meanwhile (the main
   *  display's first free spot); never kept, so it goes home when its
   *  display is back. */
  standIns: Map<string, PinRect>;
}

/**
 * The pins laid out (when the owner opens their windows): each stays where
 * the user put it, inside the margins of its display (`homeOf`: the work
 * area of the display holding the rect, null when none does), unless it
 * covers a pin kept before it. A pin whose display is missing keeps its
 * record and stands in on `main`. `fixed` holds what Denker never moves
 * (Apple's widgets, desktop icons).
 */
export function layOutPins(
  pins: readonly PinToLayOut[],
  homeOf: (rect: PinRect) => PinRect | null,
  main: PinRect | null,
  fixed: readonly OccupiedRect[],
): PinLayout {
  const layout: PinLayout = { moved: new Map(), standIns: new Map() };
  const homed = pins.flatMap((pin) => {
    const home = homeOf(pin.rect);
    return home ? [{ ...pin, home }] : [];
  });
  const kept: PinRect[] = [];
  homed.forEach((pin, index) => {
    let rect = clampPinRect(pin.rect, pin.home, fixed);
    if (kept.some((other) => overlaps(rect, other))) {
      const waiting = homed.slice(index + 1).map((other) => other.rect);
      rect = firstFreeSpot(rect, pin.home, [...fixed, ...kept, ...waiting]);
    }
    kept.push(rect);
    if (!sameRect(rect, pin.rect)) layout.moved.set(pin.frameId, rect);
  });
  if (!main) return layout;
  for (const pin of pins) {
    if (homeOf(pin.rect)) continue;
    const standIn = firstFreeSpot(pin.rect, main, [...fixed, ...kept]);
    kept.push(standIn);
    layout.standIns.set(pin.frameId, standIn);
  }
  return layout;
}

function sameRect(a: PinRect, b: PinRect): boolean {
  return (
    a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height
  );
}
